import _ from 'lodash'
import Guard from 'arg-guard'

import ApiData from './api-data'
import Filters from './filters'
import Savings from './savings'

import Config from '@/app.config'
import Logger from '@/logger'
import RenovationsManager from '@/renovations-manager'

import Store from '@/store'
import { ACTIONS } from '@/store/actions'
import { MUTATIONS } from '@/store/mutations'

import CALCULATION_STATES from '@/models/CALCULATION_STATES'
import CONTEXT_TYPES from '@/auth/CONTEXT_TYPES'
import FILTER_NAMES, { MOTIVATION_FILTERS } from './FILTER_NAMES'
import MOTIVATIONS from '@/models/MOTIVATIONS'
import RENOVATION_STATUS from '@/models/RENOVATION_STATUS'

import Project from '@/models/project'
import Renovation from '@/models/renovation'

// noinspection ES6CheckImport
import { Error } from '@/helpers/global'
import RenovationNameGenerator from '@/renovation-name-generator'

const loggingCategory = 'Improvements Plan Builder'
const loggingCategoryFlow = 'Renovation Lifecycle'

/**
 * Builds the parameters for passing to filters to support dynamic parameters and state based parameters.
 * @param {string} filterName The name of the function to filter the improvements by.
 * @param {Object} params Optional parameters to pass to the filter function.
*/
const buildParams = function(filterName, params = {}) {
    const retParams = _.cloneDeep(params)

    if (filterName === FILTER_NAMES.withinBudget)
        if (!retParams.budget) retParams.budget = Store.state.improvements.currentProject.budget

    return retParams
}

/**
 * Filter a set of improvements using the provided filter method(s).
 * @param {Array<Improvement>} sourceImprovements The source improvements to filter.
 * @param {string|Array<string>} filters The name of the function / An array of function names to filter the improvements by.
 * @param {Object} params Optional parameters to pass to the filter function(s).
 */
const filterImprovements = function(sourceImprovements, filters, params = {}) {
    Guard.throwIf(filters, 'filters')

    const filterQueue = Array.isArray(filters) ? filters : [ filters ]
    let improvements = _.cloneDeep(sourceImprovements)

    const withoutSavings = improvements
        .filter(imp => !imp.savings || imp.savings.EPCScore === 'none' && imp.savings.carbonDioxide === 'none' && imp.savings.fuelBill === 'none')

    if (withoutSavings.length > 0)
        Logger.logObject(withoutSavings, 'Improvements without any savings found', loggingCategory)

    if (improvements.length > 0 && withoutSavings.length === improvements.length)
        throw new Error('No savings data found to filter improvements with')

    filterQueue.forEach(filter => {
        const filterParams = buildParams(filter, params)
        Logger.logObject({ filter, filterParams, improvements }, 'Applying filter to improvements', loggingCategory)
        improvements = Filters[filter](improvements, filterParams)
        Logger.logObject({ filter, filterParams, improvements }, 'Filtered improvements', loggingCategory)
    })

    return improvements
}

/**
 * Gets the name of the filter associated to a given motivation key.
 * @param {string} key The key / reference stored to determine the motivation.
 */
const getFilterFromKey = function(key) {
    switch (key) {
        case MOTIVATIONS.carbonSaving:
            return FILTER_NAMES.reduceCo2Emissions
        case MOTIVATIONS.efficiencyImproving:
            return FILTER_NAMES.mostEfficient
        case MOTIVATIONS.fuelBill:
            return FILTER_NAMES.reduceFuelBill
        case MOTIVATIONS.sapScore:
            return FILTER_NAMES.improveEpc
        case MOTIVATIONS.targetNetZero:
            return FILTER_NAMES.aim4ZeroCarbon
        case MOTIVATIONS.targetSapC:
            return FILTER_NAMES.aim4SapC
        default:
            throw new TypeError(`The provided motivation key '${ key }' does not have a matching filter`)
    }
}

/*
 * The Plan Builder Manager deals with interactions with the improvements service and core builder business logic.
 */
const PlanBuilderManager = {

    ApiData,
    Filters,
    Savings,

    /**
    * Marks an improvement as selected.
    * @param {Improvement} improvement
    * @returns {Promise<boolean>} An flag, indicating success.
    */
    async addImprovementToProjectAsync(improvement) {
        Logger.trackMethod('addImprovementToProjectAsync', 'PlanBuilderManager', { improvement })
        Guard.throwIf(improvement, 'improvement')

        try {
            await Store.dispatch(ACTIONS.IMPROVEMENTS.selectImprovements, [ improvement ])
            Store.commit(MUTATIONS.IMPROVEMENTS.setCurrentProjectStatus, RENOVATION_STATUS.planning)
            const metricsResults = await Savings.updateMetricsAsync()

            if (metricsResults.partial && metricsResults.notApplied.find(imp => imp.id === improvement.id))
                await Store.dispatch(ACTIONS.IMPROVEMENTS.deselectImprovements, [ improvement ])
            else
                Logger.logObject(improvement, 'Improvement added to project', loggingCategory)

            await RenovationsManager.upsertInPlanningAsync()
            return metricsResults.succeeded
        } catch (err) {
            Logger.log(`Unable to add improvement to project: ${ err.message }`, loggingCategory)
            throw err
        }
    },

    /**
     * Apply recommendations to the project improvements utilising the specified filter function.
     * @param {string|Array<string>} filters The name of the function / An array of function names to filter the improvements by.
     * @param {Object} params Optional parameters to pass to the filter function(s).
     * @returns {Promise}
     */
    async applyRecommendationsAsync(filters, params = {}) {
        Logger.trackMethod('applyRecommendationsAsync', 'PlanBuilderManager', { filters, params })
        Guard.throwIf(filters, 'filters')
        const project = Project.ensure(Store.state.improvements.currentProject)
        Logger.logObject({ initialImprovements: project.availableImprovements, filters, params }, 'Applying recommendations', loggingCategory)
        const recommendations = filterImprovements(project.availableImprovements, filters, params)
        Logger.logObject({ filtered: recommendations }, 'Filtered recommendations', loggingCategory)
        await Store.dispatch(ACTIONS.IMPROVEMENTS.clearRecommendations, project.recommendedImprovements)
        await Store.dispatch(ACTIONS.IMPROVEMENTS.recommendImprovements, recommendations)
    },

    /**
     * Apply selections to the project improvements utilising the specified filter function.
     * @param {string|Array<string>} filters The name of the function / An array of function names to filter the improvements by.
     * @param {Object} params Optional parameters to pass to the filter function(s).
     * @returns {Promise}
     */
    async applySelectionsAsync(filters, params = {}) {
        Logger.trackMethod('applySelectionsAsync', 'PlanBuilderManager', { filters, params })
        Guard.throwIf(filters, 'filters')
        const project = Project.ensure(Store.state.improvements.currentProject)
        Logger.logObject({ initialImprovements: project.availableImprovements, filters, params }, 'Applying selections', loggingCategory)
        const selections = filterImprovements(project.availableImprovements, filters, params)
        Logger.logObject({ filtered: selections }, 'Filtered selections', loggingCategory)
        await Store.dispatch(ACTIONS.IMPROVEMENTS.deselectImprovements, project.addedImprovements)
        await Store.dispatch(ACTIONS.IMPROVEMENTS.selectImprovements, selections)
    },

    /**
     * Sorts the improvements according to the provided filter.
     * @param {string} filter The name of the filter function to sort the improvements by.
     * @returns {Promise}
     */
    async sortImprovementsAsync(filter) {
        Logger.trackMethod('sortImprovementsAsync', 'PlanBuilderManager', { filter })
        Guard.throwIfNotIn(filter, MOTIVATION_FILTERS, 'filter')

        const project = Project.ensure(Store.state.improvements.currentProject)
        Logger.logObject({ initialImprovements: project.availableImprovements, filter }, 'Sorting improvements', loggingCategory)
        const sorted = filterImprovements(project.availableImprovements, filter, { includeAlts: true })
        Logger.logObject({ sorted }, 'Sorted improvements', loggingCategory)
        await Store.dispatch(ACTIONS.IMPROVEMENTS.sortImprovements, sorted)
    },

    /**
     * Clears the stored improvement data for the current project.
     * @returns {Promise<boolean>}
     */
    async clearAsync() {
        Logger.trackMethod('clearAsync', 'PlanBuilderManager')
        try {
            await Store.dispatch(ACTIONS.IMPROVEMENTS.clearProjectData)
            Logger.log('Project data cleared', loggingCategory)
            return true
        } catch (err) {
            Logger.log(`Unable to clear project data: ${ err.message }`, loggingCategory)
            throw err
        }
    },

    /**
     * Filters the improvements displayed in the UI by updating the state stored filter parameters.
     * This filter data isn't persisted into local storage or the project and is purely for the UI.
     * @param {string} filterKey The type of filter to set.
     * @param {boolean|{ name: string, value: any }} value The value to apply to the filter. Assumes 'true' if not provided.
     */
    filterDisplayedImprovements(filterKey, value = true) {
        Logger.trackMethod('filterDisplayedImprovements', 'PlanBuilderManager', { filterKey, value })
        Store.commit(MUTATIONS.IMPROVEMENTS.setFilter, { by: filterKey, value })
    },

    /**
     * Filters the improvements displayed in the UI by updating the state stored array of segment categories to show.
     * This filter data isn't persisted into local storage or the project and is purely for the UI.
     * @param {Array<KeymapEntry>|null} segmentKeymap The scheme specific segment keymap.
     * @param {Array<string>} visibleSegments AN array of segment keys to show improvements for.
     */
    filterDisplayedImprovementsBySegment(segmentKeymap, visibleSegments = []) {
        Logger.trackMethod('filterDisplayedImprovementsBySegment', 'PlanBuilderManager', { segmentKeymap, visibleSegments })
        Store.commit(MUTATIONS.IMPROVEMENTS.setVisibleSegments, { segmentKeymap, visibleSegments })
    },

    /**
     * loads the project data into the VUEX store.
     * @param {number} addressId
     * @param {boolean} initialise
     * @param {boolean} force
     * @param {boolean} persistSelectedImprovements
     * @returns {Promise<boolean>} A flag, indicating success.
     */
    async loadProjectIntoStoreAsync(addressId,
        { initialise = false, force = false, persistSelectedImprovements = false } = { initialise: false, force: false, persistSelectedImprovements: true }) {

        /**
         * @returns {Promise}
         */
        const autoApplyRecommendationsAsync = async() => {
            const currentProject = Store.state.improvements.currentProject
            if (currentProject.motivation) {
                const motivationFilter = getFilterFromKey(currentProject.motivation)
                Logger.logObject(currentProject.motivation, 'Motivation route active', loggingCategory)
                await this.applyRecommendationsAsync([ motivationFilter, 'withinBudget' ])
                await this.sortImprovementsAsync(motivationFilter)
            } else {
                await this.applyRecommendationsAsync('withinBudget')
            }
        }

        /**
         * @returns {Promise<boolean>}
         */
        const determineProjectNameAsync = async() => {
            const isNotGuest = Store.state.authContext !== null && Store.state.authContext.type !== CONTEXT_TYPES.guest
            Logger.log(`Naming Project for ${ isNotGuest ? Store.state.authContext.type : CONTEXT_TYPES.guest }`, loggingCategoryFlow)
            Logger.logObject(Store.state.improvements.currentProject, 'Determining Project Name', loggingCategory)

            Logger.log('Considering setting a new plan name...', loggingCategoryFlow)
            const existingPlanName = Store.state.improvements.currentProject.name

            if (!existingPlanName) {
                Logger.log('Existing plan name not found', loggingCategoryFlow)
                const nextName = isNotGuest
                    ? await RenovationNameGenerator.generateNextAsync(addressId)
                    : Renovation.DEFAULT_NAME
                Store.commit(MUTATIONS.IMPROVEMENTS.setCurrentProjectName, nextName)
                Store.commit(MUTATIONS.IMPROVEMENTS.setImprovementCalculationState, CALCULATION_STATES.noOperation)
                Logger.log(`Plan initialised with name ${ nextName }`, loggingCategory)
                Logger.log(`Plan initialised with name ${ nextName }`, loggingCategoryFlow)
                Logger.logObject(Store.state.improvements.currentProject, 'Project name updated', loggingCategory)
                return true
            }

            Logger.log(`Existing plan name found ${ existingPlanName }`, loggingCategoryFlow)
            return false
        }

        /**
         * @returns {Promise<boolean>}
         */
        const initialiseProjectDataAsync = async() => {
            Logger.log('Initialising Project', loggingCategoryFlow)
            Logger.logObject(Store.state.improvements.currentProject, 'Initialising project data', loggingCategory)

            await autoApplyRecommendationsAsync()
            await this.applySelectionsAsync('recommended')
            await Store.dispatch(ACTIONS.IMPROVEMENTS.flagCurrentProjectInitialised)

            await RenovationsManager.upsertInPlanningAsync()

            Logger.logObject(Store.state.improvements.currentProject, 'Project data updated', loggingCategory)
            const result = await Savings.tryUpdatingMetricsAsync()
            return result && result.succeeded
        }

        /**
         * @returns {Promise<boolean>}
         */
        const loadProjectFromApiAsync = async() => {
            const currentProject = Store.state.improvements.currentProject

            try {
                Logger.log('Loading Project - Initial Savings', loggingCategoryFlow)
                await Savings.tryLoadingInitialSavings()

                Logger.log('Loading Project - Initiatives', loggingCategoryFlow)
                const result = await ApiData.loadInitiativesAsync(addressId)
                Logger.logObject(result, 'Loading Project - Initiatives (loaded)', loggingCategoryFlow)

                Logger.log('Loading Project - Improvements', loggingCategoryFlow)
                const improvements = result.improvements ? result.improvements : []
                if (currentProject.addedImprovements && persistSelectedImprovements) {
                    const addedIds = currentProject.addedImprovements.map(ai => ai.id)
                    improvements.forEach(imp => {
                        if (addedIds.includes(imp.id)) imp.accepted = true
                    })
                }
                Logger.logObject(improvements, 'Loading Project - Improvements (loaded)', loggingCategoryFlow)

                Logger.log('Loading Project - Updating state', loggingCategoryFlow)
                Store.commit(MUTATIONS.IMPROVEMENTS.setCurrentProjectImprovements, {
                    sourceData: improvements,
                    dateStamp: result.dateStamp,
                    baseMetrics: Store.state.metrics.data,
                })
                Logger.logObject(currentProject, 'Loading Project (state updated)', loggingCategoryFlow)

                const retVal = initialise && improvements.length ? await initialiseProjectDataAsync() : true
                Store.commit(MUTATIONS.clearProjectLastLoaded)
                return retVal
            } catch (err) {
                Logger.log('Unable to load project', loggingCategoryFlow)
                Logger.log(`Unable to load project for the property with ID ${ addressId }: ${ err.message }`, loggingCategory)
                Store.commit(MUTATIONS.clearProjectLastLoaded)
                throw err
            }
        }

        /**
         * @returns {Promise<boolean>}
         */
        const refreshProjectDataAsync = async() => {
            Logger.log('Refreshing Project', loggingCategoryFlow)
            Logger.logObject(Store.state.improvements.currentProject, 'Existing project data found in state', loggingCategory)
            await autoApplyRecommendationsAsync()

            await RenovationsManager.upsertInPlanningAsync()
            Logger.logObject(Store.state.improvements.currentProject, 'Project data refreshed', loggingCategory)
            const result = await Savings.tryUpdatingMetricsAsync()
            return result && result.succeeded
        }

        Logger.trackMethod('loadProjectIntoStoreAsync', 'PlanBuilderManager',
            { addressId, initialise, force, persistSelectedImprovements })

        Guard.throwIf(addressId, 'addressId')

        const lastLoaded = Store.state.projectLastLoaded
        const allowedLoadWindow = lastLoaded
            ? lastLoaded + Config.parallelLoadPreventedIfLessThan
            : Number.MAX_VALUE
        const nowInSeconds = Date.now() / 1000
        if (lastLoaded !== null && allowedLoadWindow >= nowInSeconds) {
            Logger.log('Project load is already in progress...', loggingCategoryFlow)
            return true
        }

        Store.commit(MUTATIONS.setProjectLastLoaded)
        Logger.log('Loading project', loggingCategoryFlow)

        const currentProject = Store.state.improvements.currentProject
        let retVal = false
        force = force || await ApiData.isLocalDataOutOfSyncAsync(addressId, currentProject.asOf)

        if (!force && currentProject && currentProject.initialised) {
            retVal = await refreshProjectDataAsync()
        } else if (!force && initialise && currentProject.improvements.length) {
            await determineProjectNameAsync()
            retVal = await initialiseProjectDataAsync()
        } else if (!force && !initialise && currentProject.improvements.length) {
            Logger.logObject(currentProject, 'Data already loaded but not yet initialised - call with { initialised: true } to do so.', loggingCategory)
            retVal = false
        } else {
            Logger.logObject(currentProject, 'Loading Project', loggingCategoryFlow)
            await Store.dispatch(ACTIONS.IMPROVEMENTS.clearCurrentProjectLoadFailure)
            await determineProjectNameAsync()
            retVal = await loadProjectFromApiAsync()
        }

        Store.commit(MUTATIONS.clearProjectLastLoaded)
        return retVal
    },

    /**
     * loads the project data into the VUEX store from a provided renovation.
     * @param {Renovation} renovation The renovation to load into a project.
     * @returns {Promise<Project>} The stored project object.
     * @throws {Error}
     */
    async loadProjectFromRenovationAsync(renovation) {
        Logger.trackMethod('loadProjectFromRenovationAsync', 'PlanBuilderManager', { renovation })
        Guard.throwIf(renovation, 'renovation')
        Logger.logObject(renovation, 'Loading project from renovation', loggingCategoryFlow)

        try {
            const currentProject = new Project(renovation, true)
            Store.commit(MUTATIONS.IMPROVEMENTS.setCurrentProject, currentProject)

            const succeeded = await this.loadProjectIntoStoreAsync(renovation.addressId, { force: true, persistSelectedImprovements: true })
            if (!succeeded) {
                // noinspection ExceptionCaughtLocallyJS
                throw new Error('Unable to load renovation plan data into current project.')
            }

            return Store.state.improvements.currentProject
        } catch (err) {
            const renovationId = renovation ? renovation.id : 'NEW'
            Logger.log(`Unable to load project into store for the renovation ${ renovationId }: ${ err.message }`, loggingCategory)
            throw err
        }
    },

    /**
     * Modify the budget for the plan.
     * @param {number} budget
     * @returns {Promise<boolean>} An flag, indicating success.
     * @throws {Error}
     */
    async modifyBudgetAsync(budget) {
        Logger.trackMethod('modifyBudgetAsync', 'PlanBuilderManager', { budget })
        Guard.throwIf(budget, 'budget')
        Logger.log(`Changing available budget to £${ budget }...`, loggingCategory)

        try {
            let project = Store.state.improvements.currentProject
            if (budget !== project.budget)
                Store.commit(MUTATIONS.IMPROVEMENTS.setCurrentProjectStatus, RENOVATION_STATUS.planning)

            Store.commit(MUTATIONS.IMPROVEMENTS.setBudget, budget)

            project = Store.state.improvements.currentProject
            if (!project.initialised || !project.improvements) return true

            Logger.logObject({ motivationFilter: project.motivation, budget }, 'Filtering after budget set', loggingCategory)
            if (project.motivation) {
                const motivationFilter = getFilterFromKey(project.motivation)
                await this.applyRecommendationsAsync([ motivationFilter, 'withinBudget' ], { budget })
                await this.sortImprovementsAsync(motivationFilter)
            } else {
                await this.applyRecommendationsAsync('withinBudget', { budget })
            }

            const metricsResults = await Savings.updateMetricsAsync()
            await RenovationsManager.upsertInPlanningAsync()
            return metricsResults.succeeded
        } catch (err) {
            Logger.log(`Unable to modify budget: ${ err.message }`, loggingCategory)
            throw err
        }
    },

    /**
     * Removes an improvement from the selected list.
     * @param {Improvement} improvement
     * @returns {Promise<boolean>}
     */
    async removeImprovementFromProjectAsync(improvement) {
        Logger.trackMethod('removeImprovementFromProjectAsync', 'PlanBuilderManager', { improvement })
        Guard.throwIf(improvement, 'improvement')

        try {
            let succeeded = true
            await Store.dispatch(ACTIONS.IMPROVEMENTS.deselectImprovements, [ improvement ])
            Store.commit(MUTATIONS.IMPROVEMENTS.setCurrentProjectStatus, RENOVATION_STATUS.planning)

            Logger.logObject(improvement, 'Improvement removed from project', loggingCategory)
            succeeded = await Savings.updateMetricsAsync().succeeded
            await RenovationsManager.upsertInPlanningAsync()
            return succeeded
        } catch (err) {
            Logger.log(`Unable to remove improvement from project: ${ err.message }`, loggingCategory)
            throw err
        }
    },

    /**
     * Modify the key motivation for the plan.
     * @param {string} motivationKey
     * @returns {Promise<boolean>}
     */
    async setMotivationAsync(motivationKey) {
        Logger.trackMethod('setMotivationAsync', 'PlanBuilderManager', { motivationKey })
        Guard.throwIf(motivationKey, 'motivationKey')
        Logger.log(`Setting project motivation to ${ motivationKey }...`, loggingCategory)

        try {
            let project = Store.state.improvements.currentProject
            if (motivationKey !== project.motivation)
                Store.commit(MUTATIONS.IMPROVEMENTS.setCurrentProjectStatus, RENOVATION_STATUS.planning)

            Store.commit(MUTATIONS.IMPROVEMENTS.setMotivation, motivationKey)

            project = Store.state.improvements.currentProject
            if (!project.initialised || !project.improvements) return true

            const motivationFilter = getFilterFromKey(motivationKey)
            await this.applyRecommendationsAsync(project.budget ? [ motivationFilter, 'withinBudget' ] : motivationFilter)
            await this.sortImprovementsAsync(motivationFilter)

            const metricsResults = await Savings.updateMetricsAsync()
            await RenovationsManager.upsertInPlanningAsync()
            return metricsResults.succeeded
        } catch (err) {
            Logger.log(`Unable to set motivation: ${ err.message }`, loggingCategory)
            throw err
        }
    },

    /**
     * Sets a note for the current project.
     * @param {string} key
     * @param {?string} value
     * @returns {Promise<Array<{key, value}>>} The project notes after being changed.
     */
    async setNoteAsync(key, value) {
        Logger.trackMethod('setNoteAsync', 'PlanBuilderManager', { key, value })
        Guard.throwIf(key, 'key')
        Logger.log(`Setting note ${ key } to ${ value }`, loggingCategory)

        try {
            Store.commit(MUTATIONS.IMPROVEMENTS.setCurrentProjectNote, { key, value })
            Store.commit(MUTATIONS.IMPROVEMENTS.setCurrentProjectStatus, RENOVATION_STATUS.planning)
            await RenovationsManager.upsertInPlanningAsync()
            return Store.state.improvements.currentProject.notes
        } catch (err) {
            Logger.log(`Unable to set note: ${ err.message }`, loggingCategory)
            throw err
        }
    },

    /**
     * Sets a service offering for the current project.
     * @param {string} key
     * @param {?string} value
     * @returns {Promise<Array<{key, value}>>} The project service offerings after being changed.
     */
    async setServiceOfferingAsync(key, value) {
        Logger.trackMethod('setServiceOfferingAsync', 'PlanBuilderManager', { key, value })
        Guard.throwIf(key, 'key')
        Logger.log(`Setting ServiceOffering ${ key } to ${ value }`, loggingCategory)

        try {
            Store.commit(MUTATIONS.IMPROVEMENTS.setCurrentProjectServiceOffering, { key, value })
            Store.commit(MUTATIONS.IMPROVEMENTS.setCurrentProjectStatus, RENOVATION_STATUS.planning)
            await RenovationsManager.upsertInPlanningAsync()
            return Store.state.improvements.currentProject.services
        } catch (err) {
            Logger.log(`Unable to set note: ${ err.message }`, loggingCategory)
            throw err
        }
    },
}

export default PlanBuilderManager
